import axios, { Method, BaseURL } from '@/apps/open/utils/axios'
import { IAppInfo } from '@/apps/open/store/modules/app/types'

const isPrivate = process.env.VUE_APP_IS_PRIVATE
const deployEnv = process.env.VUE_APP_DEPLOY_ENVIRONMENT

/**
 * 应用列表
 * @param params
 */
export function appList (params: any) {
  return axios({ url: '/api/my/apps', method: Method.Get, params })
}

/**
 * 应用信息
 * @param app_id
 */
export function getAppInfo (app_id: string) {
  return axios({ url: '/api/my/app', method: Method.Get, params: { app_id } })
}

/**
 * 获取应用认证方式配置
 * @param app_id
 */
export function getAppAuthConfigs (app_id: string) {
  return axios({
    url: '/oauth/v1/auth_cfg',
    method: Method.Get,
    params: { app_id }
  })
}

/**
 * 获取应用同步方式配置
 * @param data
 */
export function saveAppSyncConfigs (data: any) {
  return axios({
    url: '/oauth/v1/sync_cfg',
    method: Method.Post,
    data
  })
}

/**
 * 创建应用认证方式配置
 * @param data
 */
export function saveAppAuthConfigs (data: any) {
  return axios({
    url: '/oauth/v1/auth_cfg',
    method: Method.Post,
    data: data
  })
}

/**
 * 更新应用认证方式配置
 * @param data
 */
export function updateAppAuthConfigs (data: any) {
  return axios({
    url: '/oauth/v1/auth_cfg',
    method: Method.Patch,
    data: data
  })
}

/**
 * 删除应用认证方式配置
 * @param data
 */
export function deleteAppAuthConfigs (app_id: string, open_ssid: string) {
  return axios({
    url: `/api/my/app/${app_id}/certification`,
    method: Method.Delete,
    data: { open_ssid }
  })
}

/**
 * 检查企业授权
 * @param appid
 * @param company_id
 */
export function checkAppCompanyAuthorizable (
  app_id: string,
  company_id: string,
  scope: string
) {
  const priApiUrl = deployEnv === 'ksa' ? '/openapi/oauthapi/v3/company/app_company_authorizable' : '/koauthapi/v1/company/authorized'
  return axios({
    url: isPrivate ? `${priApiUrl}` : `/oauthapi/v3/company/app_company_authorizable`,
    method: Method.Get,
    params: { app_id, company_id, scope },
    baseURL: isPrivate ? '' : BaseURL.OpenApi
  })
}

/**
 * 更新应用信息
 * @param app
 */
export function updateAppInfo (app: IAppInfo) {
  return axios({ url: '/api/my/app/_update', method: Method.Post, data: app })
}

/**
 * 创建应用
 * @param app
 */
export function addApp (app: IAppInfo) {
  return axios({ url: '/api/my/app/_create', method: Method.Post, data: app })
}

/**
 * 获取账号公司列表
 * @param app
 */
export function getCompanyList (developer_id: number | string) {
  return axios({ url: '/api/my/company/list', method: Method.Get, params: developer_id ? { developer_id } : Object.create(null) })
}

/**
 * 获取同步方式配置
 * @param app_id
 */
export function getSyncInfo (app_id: string) {
  return axios({
    url: '/oauth/v1/sync_cfg',
    method: Method.Get,
    params: { app_id }
  })
}

/**
 * 获取企业id
 * @param app_id
 */
export function getCompanyId (app_id: string) {
  return axios({
    url: '/oauth/v1/app_ext',
    method: Method.Get,
    params: { app_id }
  })
}

/**
 * 获取企业超级管理员
 * @param open_comp_id
 */
export function getSuperAdminInfo (open_comp_id: string, app_id: string) {
  return axios({
    url: `/oauth/v1/companies/${open_comp_id}/super_admin/info`,
    method: Method.Get,
    params: { app_id }
  })
}

/**
 * 检测应用名称是否已存在
 * @param app_name
 */
export function checkAppNameIsExist (app_name: string) {
  return axios({
    url: '/api/app/name_is_exist',
    method: Method.Get,
    params: { app_name }
  })
}

/**
 * 删除应用
 * @param app_id
 * @param open_ssid
 */
export function deleteApp (app_id: string, open_ssid: string) {
  return axios({
    url: '/api/my/app',
    method: Method.Delete,
    data: { app_id, open_ssid }
  })
}

/**
 * 开放平台所有功能列表
 */
export function getFeatureList (app_id: string) {
  return axios({
    url: '/api/functions',
    method: Method.Get,
    params: { app_id }
  })
}

/**
 * 服务商功能申请列表
 * @param app_id
 */
export function getApplyFeatureList (app_id: string) {
  return axios({
    url: '/api/my/apps_and_functions',
    method: Method.Get,
    params: { app_id }
  })
}

/**
 * 申请功能
 * @param app_id
 * @param function_id
 */
export function applyFeature (app_id: string, function_id: string) {
  return axios({
    url: '/api/my/apps_and_functions/_apply',
    method: Method.Post,
    data: { app_id, function_id }
  })
}

/**
 * 添加应用开发人员
 * @param app_id
 * @param user_ids
 */
export function addAppDeveloper (app_id: string, user_ids: string) {
  return axios({
    url: '/api/my/app/admin/add',
    method: Method.Post,
    data: { app_id, user_ids }
  })
}

/**
 * 删除应用开发人员
 * @param app_id
 * @param user_id
 */
export function removeAppDeveloper (app_id: string, user_id: number) {
  return axios({
    url: '/api/my/app/admin',
    method: Method.Delete,
    data: { app_id, user_id }
  })
}

/**
 * 用户白名单列表（测试人员）
 * @param params
 */
export function getUserWhitelist (params: any) {
  return axios({
    url: '/api/my/app/user_white_list',
    method: Method.Get,
    params
  })
}

/**
 * 添加用户白名单
 * @param app_id
 * @param userid
 */
export function addUserWhitelist (app_id: string, userid: string) {
  return axios({
    url: '/api/my/app/user_white_list',
    method: Method.Post,
    data: { app_id, userid }
  })
}

/**
 * 删除用户白名单
 * @param app_id
 * @param userid
 */
export function removeUserWhitelist (app_id: string, userid: string) {
  return axios({
    url: '/api/my/app/user_white_list',
    method: Method.Delete,
    data: { app_id, userid }
  })
}

/**
 * 域名白名单
 * @param app_id
 */
export function getDomainWhitelist (app_id: string) {
  return axios({
    url: `/serviceapi/openapi/domain/${app_id}`,
    method: Method.Get
  })
}

/**
 * 更新域名白名单
 * @param app_id
 * @param domain_list
 */
export function updateDomainWhitelist (app_id: string, domain_list: string[]) {
  const data = {
    domain1: domain_list[0],
    domain2: domain_list[1] || '',
    domain3: domain_list[2] || ''
  }
  return axios({
    url: `/serviceapi/openapi/domain/${app_id}`,
    method: Method.Post,
    data
  })
}

/**
 * IP白名单
 * @param app_id
 */
export function getIpWhitelist (app_id: string) {
  return axios({
    url: '/api/my/app/ip_white_list',
    method: Method.Get,
    params: { app_id }
  })
}

/**
 * 添加IP白名单
 * @param app_id
 * @param ip_list
 */
export function appendIPWhitelist (app_id: string, ip_list: string[]) {
  return axios({
    url: '/api/my/app/ip_white_list/_append',
    method: Method.Post,
    data: { app_id, ip_list }
  })
}

/**
 * 删除IP白名单
 * @param app_id
 * @param ip_list
 */
export function removeIPWhitelist (app_id: string, ip_list: string[]) {
  return axios({
    url: '/api/my/app/ip_white_list/_remove',
    method: Method.Post,
    data: { app_id, ip_list }
  })
}

/**
 * 获取APP KEY
 * @param app_id
 * @param open_ssid
 */
export function getAppKey (app_id: string, open_ssid?: string) {
  return axios({
    url: '/api/my/app/key',
    method: Method.Get,
    params: { app_id, open_ssid }
  })
}

/**
checkAppCompanyAuthorizable * 重置 APP KEY
 * @param app_id
 * @param open_ssid
 */
export function resetAppKey (app_id: string, open_ssid?: string) {
  return axios({
    url: '/api/my/app/key/_update',
    method: Method.Post,
    data: { app_id, open_ssid }
  })
}

/**
 * 查询 APP 企业授权通知地址
 * @param app_id
 */
export function getCorpAuthNotifyUrl (app_id: string) {
  return axios({
    url: '/api/app/corp_auth_notify_url',
    method: Method.Get,
    params: { app_id }
  })
}

/**
 * 查询 APP 消息地址
 * @param app_id
 */
export function getInceptMsgAdress (app_id: string) {
  return axios({
    url: '/api/app/open_msg_notify_url',
    method: Method.Get,
    params: { app_id }
  })
}

/**
 * 查询 APP 额外信息
 * @param app_id
 */
export function getAppExtraInfo (app_id: string) {
  return axios({
    url: `/api/my/app/${app_id}/extension`,
    method: Method.Get
  })
}

/**
 * 修改 APP 额外信息的Topic
 * @param app_id
 * @param topic
 */
export function setAppTopic (app_id: string, topic: string) {
  return axios({
    url: `/api/my/app/${app_id}/topic`,
    method: Method.Put,
    data: { topic }
  })
}

/**
 * 设置 APP 企业授权通知地址
 * @param app_id
 * @param url
 */
export async function setCorpAuthNotifyUrl (app_id: string, url: string) {
  return axios({
    url: '/api/app/corp_auth_notify_url',
    method: Method.Put,
    data: { app_id, url }
  })
}

/**
 * 设置 APP 消息地址
 * @param app_id
 * @param url
 */
export async function setInceptMsgAdress (app_id: string, url: string) {
  return axios({
    url: '/api/app/open_msg_notify_url',
    method: Method.Put,
    data: { app_id, url }
  })
}

/**
 * 获取回调地址配置
 */
export async function getCallbackInfo (app_id: string) {
  return axios({
    url: `/api/me/app/${app_id}/doc_config`,
    method: Method.Get
  })
}

/**
 * 更新回调地址配置
 */
export async function updateCallbackInfo (app_id: string, params: any) {
  return axios({
    url: `/api/me/app/${app_id}/doc_config`,
    method: Method.Put,
    data: params
  })
}

/**
 * 判断是否存在应用空加
 * @param app_id 应用id
 * @param company_id 企业id
 */
export async function getHasExistSpace (app_id: string, company_id: string) {
  return axios({
    url: `/plus/drive/v1/u/app/${app_id}/company/${company_id}/exist`,
    method: Method.Get
  })
}

/**
 * 获取消息订阅配置
 * @param app_id 应用id
 */
export async function getMsgSubscribe (app_id: string) {
  return axios({
    url: `/api/me/app/${app_id}/message_subscription`,
    method: Method.Get
  })
}

/**
 * 配置消息订阅
 * @param app_id 应用id
 * @param params 配置数据
 */
export async function setMsgSubscribe (app_id: string, params: any) {
  return axios({
    url: `/api/me/app/${app_id}/message_subscription`,
    method: Method.Put,
    data: params
  })
}

/**
 * 获取文档编辑回调配置
 * @param app_id 应用id
 */
export async function getDocEditCbConfig (app_id: string) {
  return axios({
    url: `/api/me/app/${app_id}/online_edit`,
    method: Method.Get
  })
}

/**
 * 设置文档编辑回调配置
 * @param app_id 应用id
 * @param params 配置数据
 */
export async function setDocEditCbConfig (app_id: string, params: any) {
  return axios({
    url: `/api/me/app/${app_id}/online_edit`,
    method: Method.Put,
    data: params
  })
}

/**
 * 获取文档预览回调配置
 * @param app_id 应用id
 */
export async function getDocPreviewCbConfig (app_id: string) {
  return axios({
    url: `/api/me/app/${app_id}/online_preview`,
    method: Method.Get
  })
}

/**
 * 设置文档预览回调配置
 * @param app_id 应用id
 * @param params 配置数据
 */
export async function setDocPreviewCbConfig (app_id: string, params: any) {
  return axios({
    url: `/api/me/app/${app_id}/online_preview`,
    method: Method.Put,
    data: params
  })
}

/**
 * 获取文档格式回调配置
 * @param app_id 应用id
 */
export async function getDocFormatCbConfig (app_id: string) {
  return axios({
    url: `/api/me/app/${app_id}/format_handle`,
    method: Method.Get
  })
}

/**
 * 设置文档格式回调配置
 * @param app_id 应用id
 * @param params 配置数据
 */
export async function setDocFormatCbConfig (app_id: string, params: any) {
  return axios({
    url: `/api/me/app/${app_id}/format_handle`,
    method: Method.Put,
    data: params
  })
}

/**
 * 文档中台，获取csrf配置
 */
export async function getCsrfWhitelistConfig (app_id: string) {
  return axios({
    url: `/api/me/app/${app_id}/csrf_white_list`,
    method: Method.Get
  })
}

/**
 * 文档中台，设置csrf配置
 * @param app_id 应用id
 * @param params 配置数据
 */
export async function setCsrfWhitelistConfig (app_id: string, params: any) {
  return axios({
    url: `/api/me/app/${app_id}/csrf_white_list`,
    method: Method.Put,
    data: params
  })
}
